import React from 'react';
import { LayersIcon, LandmarkIcon, FlameIcon, BarChart3Icon, LeafIcon, BriefcaseIcon } from 'lucide-react';
export interface CourseProgram {
  id: string;
  route: string;
  icon: React.ReactNode;
  category: string;
  title: string;
  shortTitle: string;
  description: string;
  audience: string;
  audienceList: string[];
  duration: string;
  level: string;
  cpdHours: number;
  price: number;
  modules: {
    title: string;
    topics: string[];
    duration: string;
  }[];
  regulatoryAlignment: string[];
  learningOutcomes: string[];
  certName: string;
  deliveryFormats: {
    label: string;
    desc: string;
  }[];
  caseStudies: {
    title: string;
    desc: string;
  }[];
}
export const programs: CourseProgram[] = [{
  id: 'foundations',
  route: 'course-foundations',
  icon: <LayersIcon className="w-6 h-6" data-id="element-1485" />,
  category: 'Core Program',
  title: 'ESG Foundations Program',
  shortTitle: 'ESG Foundations',
  description: 'A rigorous introduction to ESG fundamentals, GHG accounting, climate risk, and major reporting frameworks. The prerequisite for all advanced programs.',
  audience: 'All industries — compliance officers, sustainability managers, finance teams',
  audienceList: ['Compliance officers and sustainability managers', 'Finance and accounting teams', 'Operations and procurement managers', 'HR and governance professionals', 'Any professional requiring baseline ESG competency'],
  duration: '3 days',
  level: 'Foundational',
  cpdHours: 24,
  price: 1800,
  modules: [{
    title: 'ESG Fundamentals & Materiality',
    topics: ['ESG framework overview', 'Double materiality assessment', 'Stakeholder mapping'],
    duration: '5 hours'
  }, {
    title: 'GHG Accounting Basics (Scope 1, 2, 3)',
    topics: ['GHG Protocol overview', 'Scope definitions and boundaries', 'Emission factor selection'],
    duration: '6 hours'
  }, {
    title: 'Climate Risk Introduction',
    topics: ['Physical risk categories', 'Transition risk categories', 'TCFD framework overview'],
    duration: '5 hours'
  }, {
    title: 'Reporting Frameworks Overview',
    topics: ['GHG Protocol, TCFD, ISSB comparison', 'Disclosure requirements', 'Data collection basics'],
    duration: '5 hours'
  }, {
    title: 'Regulatory Landscape',
    topics: ['Uganda Climate Change Act 2021', 'International obligations', 'Compliance calendar'],
    duration: '3 hours'
  }],
  regulatoryAlignment: ['GHG Protocol', 'TCFD', 'ISSB', 'Uganda Climate Change Act'],
  learningOutcomes: ['Define ESG materiality and apply double materiality assessment methodology', 'Calculate Scope 1, 2, and 3 GHG emissions using GHG Protocol standards', 'Identify and categorise physical and transition climate risks', 'Navigate major reporting frameworks (GHG Protocol, TCFD, ISSB)', 'Understand Uganda and international climate regulatory obligations'],
  certName: 'CarbonSafe Certified ESG Professional (CESGP)',
  deliveryFormats: [{
    label: 'In-Person',
    desc: 'Kampala, Uganda. 3 consecutive days. Includes printed materials and workshop exercises.'
  }, {
    label: 'Online',
    desc: 'Self-paced with live instructor Q&A. Module access for 60 days post-enrollment.'
  }, {
    label: 'Hybrid',
    desc: 'Blended format for corporate teams. 2 days in-person, 1 day online.'
  }],
  caseStudies: [{
    title: 'Ugandan Manufacturer',
    desc: 'Conducting a first-time GHG inventory and materiality assessment for a mid-size manufacturing company.'
  }, {
    title: 'East African NGO',
    desc: 'Building an ESG reporting framework aligned with donor requirements and ISSB standards.'
  }]
}, {
  id: 'banking',
  route: 'course-banking',
  icon: <LandmarkIcon className="w-6 h-6" data-id="element-1486" />,
  category: 'Banking & Finance',
  title: 'Banking & Financial Institutions Program',
  shortTitle: 'Banking & Finance',
  description: 'Comprehensive certification covering financed emissions, PCAF methodology, IFRS S1/S2 disclosure, climate risk in credit assessment, and green taxonomy alignment.',
  audience: 'Banks, DFIs, investment firms, insurance companies, credit officers, risk teams',
  audienceList: ['Commercial bank credit officers and relationship managers', 'Risk managers and chief risk officers', 'Sustainability and ESG teams at financial institutions', 'CFOs and finance directors', 'Board members and audit committee members', 'Development finance institution staff'],
  duration: '5 days',
  level: 'Advanced',
  cpdHours: 40,
  price: 1800,
  modules: [{
    title: 'Financed Emissions & PCAF Methodology',
    topics: ['PCAF Standard overview', 'Asset class coverage', 'Data quality scoring'],
    duration: '4 hours'
  }, {
    title: 'Portfolio Carbon Accounting & Attribution',
    topics: ['Weighted average carbon intensity', 'Absolute emissions calculation', 'Sector benchmarking'],
    duration: '4 hours'
  }, {
    title: 'Loan Book Emissions Calculation',
    topics: ['Commercial real estate loans', 'Corporate and SME lending', 'Data gap management'],
    duration: '4 hours'
  }, {
    title: 'IFRS S1 & S2 for Financial Institutions',
    topics: ['Governance and strategy disclosure', 'Risk management integration', 'Metrics and targets'],
    duration: '6 hours'
  }, {
    title: 'Climate Risk in Credit Assessment',
    topics: ['Physical risk in underwriting', 'Transition risk in loan covenants', 'Sector exposure limits'],
    duration: '6 hours'
  }, {
    title: 'Stress Testing & Transition Risk Modelling',
    topics: ['NGFS scenario analysis', 'Orderly vs. disorderly transition', 'Basic stress test methodology'],
    duration: '4 hours'
  }, {
    title: 'Sustainable Finance & Green Taxonomy',
    topics: ['EU Green Taxonomy alignment', 'Sustainability-linked loans', 'Greenwashing risk management'],
    duration: '6 hours'
  }, {
    title: 'Green Bonds & Avoiding Greenwashing',
    topics: ['ICMA Green Bond Principles', 'Use of proceeds verification', 'Impact reporting'],
    duration: '6 hours'
  }],
  regulatoryAlignment: ['PCAF', 'IFRS S1/S2', 'Basel Climate Risk', 'EU Green Taxonomy', 'TCFD'],
  learningOutcomes: ['Calculate financed emissions using PCAF attribution methods across asset classes', 'Produce IFRS S1/S2 compliant climate disclosures for financial institutions', 'Integrate climate risk into credit underwriting and loan assessment processes', 'Conduct basic climate stress tests and transition risk scenario analysis', 'Identify and avoid greenwashing in green bond and sustainable finance products', 'Establish board-level climate governance and oversight frameworks'],
  certName: 'CarbonSafe Certified Climate Finance Professional (CCFP)',
  deliveryFormats: [{
    label: 'In-Person',
    desc: 'Kampala, Uganda. Cohort-based, 5 consecutive days. Includes printed materials and networking sessions.'
  }, {
    label: 'Online',
    desc: 'Self-paced with live instructor Q&A sessions. Access to recorded modules for 90 days post-enrollment.'
  }, {
    label: 'Hybrid',
    desc: 'Blended format for corporate cohorts. 3 days in-person, 2 days online. Recommended for teams of 5+.'
  }],
  caseStudies: [{
    title: 'East African DFI',
    desc: 'Building PCAF-compliant financed emissions reporting for a $2.4B loan portfolio.'
  }, {
    title: 'Commercial Bank Uganda',
    desc: 'Integrating climate risk into credit assessment for agricultural and energy sector lending under Basel climate guidance.'
  }]
}, {
  id: 'oilgas',
  route: 'course-oilgas',
  icon: <FlameIcon className="w-6 h-6" data-id="element-1487" />,
  category: 'Oil & Gas',
  title: 'Oil & Gas Climate Compliance Program',
  shortTitle: 'Oil & Gas',
  description: 'Sector-specific certification for upstream, midstream, and downstream operators. Covers methane accounting, flare quantification, Scope 3, and decarbonization pathways.',
  audience: 'Oil & gas operators, environmental managers, regulatory affairs teams',
  audienceList: ['Environmental and HSE managers', 'Regulatory affairs and compliance officers', 'Operations and engineering teams', 'Sustainability directors at energy companies', 'Government regulators overseeing petroleum operations'],
  duration: '4 days',
  level: 'Advanced',
  cpdHours: 32,
  price: 1800,
  modules: [{
    title: 'Methane Accounting & Quantification',
    topics: ['Measurement vs. estimation methods', 'Leak detection and repair (LDAR)', 'Reporting standards'],
    duration: '6 hours'
  }, {
    title: 'Flare Quantification & Reporting',
    topics: ['Flare volume measurement', 'Composition analysis', 'Regulatory reporting requirements'],
    duration: '6 hours'
  }, {
    title: 'Scope 3 Emissions in Oil & Gas',
    topics: ['Category 11 use of sold products', 'Supply chain emissions mapping', 'Scope 3 data collection'],
    duration: '6 hours'
  }, {
    title: 'Decarbonization Pathways & Net Zero Planning',
    topics: ['Abatement cost curves', 'Technology options', 'Net zero target setting'],
    duration: '6 hours'
  }, {
    title: 'Carbon Credit Integrity & Offset Verification',
    topics: ['Offset quality standards', 'Additionality and permanence', 'Verification process'],
    duration: '4 hours'
  }, {
    title: 'NEMA Environmental Audit Compliance',
    topics: ['Uganda NEMA requirements', 'Environmental audit process', 'Reporting obligations'],
    duration: '4 hours'
  }],
  regulatoryAlignment: ['GHG Protocol', 'API Compendium', 'OGCI', 'Uganda Petroleum Act'],
  learningOutcomes: ['Quantify methane emissions using measurement and estimation methodologies', 'Calculate and report flare volumes in compliance with regulatory standards', 'Map and calculate Scope 3 Category 11 emissions from sold products', 'Develop sector-specific decarbonization pathways and net zero plans', 'Assess carbon credit quality and manage offset portfolio integrity', 'Comply with NEMA environmental audit requirements in Uganda'],
  certName: 'CarbonSafe Certified Energy Climate Professional (CECP)',
  deliveryFormats: [{
    label: 'In-Person',
    desc: 'Kampala, Uganda. 4 consecutive days with field exercise components.'
  }, {
    label: 'Online',
    desc: 'Self-paced with live Q&A. Module access for 90 days.'
  }, {
    label: 'On-Site',
    desc: 'Delivered at your facility for operational teams. Minimum 8 participants.'
  }],
  caseStudies: [{
    title: 'Uganda Oil Block Operator',
    desc: 'Developing a methane accounting and LDAR programme for upstream operations in the Albertine Graben.'
  }, {
    title: 'East African Refinery',
    desc: 'Building a Scope 3 emissions inventory and decarbonization roadmap for a regional petroleum refinery.'
  }]
}, {
  id: 'manufacturing',
  route: 'course-manufacturing',
  icon: <BarChart3Icon className="w-6 h-6" data-id="element-1488" />,
  category: 'Manufacturing & Export',
  title: 'Manufacturing & Export Compliance Program',
  shortTitle: 'Manufacturing & Export',
  description: 'Designed for manufacturers and exporters facing CBAM, supply chain Scope 3 obligations, and energy intensity reduction requirements under EU trade regulations.',
  audience: 'Manufacturing firms, export companies, supply chain managers, trade compliance officers',
  audienceList: ['Export compliance and trade managers', 'Supply chain and procurement directors', 'Environmental and sustainability managers', 'Finance teams managing carbon cost exposure', 'Operations managers in energy-intensive industries'],
  duration: '3 days',
  level: 'Intermediate',
  cpdHours: 24,
  price: 1800,
  modules: [{
    title: 'CBAM Readiness & Carbon Border Tax Compliance',
    topics: ['CBAM scope and timeline', 'Embedded emissions calculation', 'CBAM certificate obligations'],
    duration: '5 hours'
  }, {
    title: 'Supply Chain Scope 3 Emissions Mapping',
    topics: ['Category 1 purchased goods', 'Supplier engagement strategy', 'Data collection tools'],
    duration: '5 hours'
  }, {
    title: 'Energy Intensity Reduction Strategies',
    topics: ['Energy audit methodology', 'Efficiency benchmarking', 'Reduction target setting'],
    duration: '5 hours'
  }, {
    title: 'Product Carbon Footprint Calculation',
    topics: ['Life cycle assessment basics', 'PCF methodology', 'Environmental product declarations'],
    duration: '5 hours'
  }, {
    title: 'EUDR Due Diligence for Agricultural Exporters',
    topics: ['Deforestation-free supply chain requirements', 'Geolocation data collection', 'Due diligence statements'],
    duration: '4 hours'
  }],
  regulatoryAlignment: ['CBAM', 'EUDR', 'GHG Protocol Scope 3', 'ISO 14064'],
  learningOutcomes: ['Calculate embedded carbon emissions for CBAM-covered products', 'Map and quantify Scope 3 Category 1 supply chain emissions', 'Conduct energy intensity assessments and set reduction targets', 'Calculate product carbon footprints using life cycle assessment methodology', 'Implement EUDR due diligence requirements for agricultural commodity exports', 'Produce audit-ready documentation for EU trade compliance'],
  certName: 'CarbonSafe Certified Export Compliance Professional (CCECP)',
  deliveryFormats: [{
    label: 'In-Person',
    desc: 'Kampala, Uganda. 3 consecutive days with practical calculation workshops.'
  }, {
    label: 'Online',
    desc: 'Self-paced with live Q&A. Module access for 60 days.'
  }, {
    label: 'Hybrid',
    desc: 'Blended format. 2 days in-person, 1 day online. Suitable for distributed teams.'
  }],
  caseStudies: [{
    title: 'Ugandan Coffee Exporter',
    desc: 'Preparing CBAM and EUDR compliance documentation for coffee exports to the EU market.'
  }, {
    title: 'East African Steel Manufacturer',
    desc: 'Calculating embedded carbon for CBAM-covered steel products and establishing a carbon cost management strategy.'
  }]
}, {
  id: 'afolu',
  route: 'course-afolu',
  icon: <LeafIcon className="w-6 h-6" data-id="element-1489" />,
  category: 'Agriculture & Forestry',
  title: 'Agriculture, Forestry & Land Use (AFOLU)',
  shortTitle: 'AFOLU',
  description: 'Certification in soil carbon accounting, AFOLU methodologies, dMRV principles, and Article 6 carbon market mechanics for land-based project developers.',
  audience: 'Agribusinesses, forestry operators, carbon project developers, land managers',
  audienceList: ['Carbon project developers and consultants', 'Agribusiness sustainability managers', 'Forestry operators and land managers', 'Development finance staff funding land-use projects', 'Government officials overseeing land-use policy'],
  duration: '4 days',
  level: 'Intermediate',
  cpdHours: 32,
  price: 1800,
  modules: [{
    title: 'Soil Carbon Basics & Measurement',
    topics: ['Soil organic carbon pools', 'Sampling methodology', 'Measurement uncertainty'],
    duration: '6 hours'
  }, {
    title: 'AFOLU Carbon Accounting Methodologies',
    topics: ['Verra VM0042', 'Gold Standard AFOLU', 'Additionality and baseline setting'],
    duration: '6 hours'
  }, {
    title: 'Digital MRV (dMRV) Principles & Tools',
    topics: ['Remote sensing basics', 'IoT sensor integration', 'Data validation workflows'],
    duration: '6 hours'
  }, {
    title: 'Article 6 Carbon Market Mechanics',
    topics: ['ITMOs and corresponding adjustments', 'Article 6.2 bilateral agreements', 'Article 6.4 mechanism'],
    duration: '6 hours'
  }, {
    title: 'REDD+ Framework & Forest Carbon',
    topics: ['REDD+ activity types', 'Reference level setting', 'Safeguards and co-benefits'],
    duration: '4 hours'
  }, {
    title: 'Smallholder Integration & Data Collection',
    topics: ['Aggregation methodologies', 'Mobile data collection', 'Community benefit sharing'],
    duration: '4 hours'
  }],
  regulatoryAlignment: ['Verra VCS', 'Gold Standard', 'UNFCCC Article 6', 'AFOLU Guidelines'],
  learningOutcomes: ['Design soil carbon sampling programmes and calculate soil organic carbon stocks', 'Apply Verra VCS and Gold Standard AFOLU methodologies to project design', 'Implement dMRV systems using remote sensing and IoT data streams', 'Navigate Article 6 carbon market mechanics and corresponding adjustment requirements', 'Develop REDD+ project frameworks with appropriate safeguards', 'Design smallholder aggregation programmes with robust data collection systems'],
  certName: 'CarbonSafe Certified AFOLU Carbon Professional (CACP)',
  deliveryFormats: [{
    label: 'In-Person',
    desc: 'Kampala, Uganda. 4 days with field visit component to an active carbon project.'
  }, {
    label: 'Online',
    desc: 'Self-paced with live Q&A. Module access for 90 days.'
  }, {
    label: 'Field-Based',
    desc: 'Delivered at project site for developer teams. Minimum 6 participants.'
  }],
  caseStudies: [{
    title: 'Uganda Agroforestry Project',
    desc: 'Designing a soil carbon MRV system for a 15,000-hectare smallholder agroforestry project using dMRV tools.'
  }, {
    title: 'East African REDD+ Programme',
    desc: 'Developing a national REDD+ reference level and MRV framework aligned with UNFCCC requirements.'
  }]
}, {
  id: 'executive',
  route: 'course-executive',
  icon: <BriefcaseIcon className="w-6 h-6" data-id="element-1490" />,
  category: 'Executive & Board',
  title: 'Executive & Board Climate Certification',
  shortTitle: 'Executive & Board',
  description: 'High-ticket certification program for C-suite executives and board members. Covers climate governance, fiduciary duty, litigation risk, and board oversight frameworks.',
  audience: 'CEOs, CFOs, Board Directors, Audit Committee Members, General Counsel',
  audienceList: ['Board directors and non-executive directors', 'Chief Executive Officers and Managing Directors', 'Chief Financial Officers and Finance Directors', 'General Counsel and Company Secretaries', 'Audit and Risk Committee members'],
  duration: '2 days',
  level: 'Executive',
  cpdHours: 16,
  price: 1800,
  modules: [{
    title: 'Climate Governance & Board Oversight Frameworks',
    topics: ['Board climate governance structures', 'Committee mandates', 'Oversight best practices'],
    duration: '3 hours'
  }, {
    title: 'Fiduciary Duty in the Climate Era',
    topics: ['Legal basis for climate consideration', 'Prudent person standard', 'Shareholder expectations'],
    duration: '3 hours'
  }, {
    title: 'Climate Litigation Risk & Director Liability',
    topics: ['Global litigation trends', 'Director personal liability cases', 'Risk mitigation strategies'],
    duration: '3 hours'
  }, {
    title: 'TCFD & ISSB Disclosure for Boards',
    topics: ['Board responsibilities under TCFD', 'ISSB S1/S2 governance requirements', 'Disclosure sign-off process'],
    duration: '3 hours'
  }, {
    title: 'Stakeholder Engagement & Investor Relations',
    topics: ['Investor climate expectations', 'Engagement frameworks', 'Proxy voting trends'],
    duration: '2 hours'
  }, {
    title: 'Setting & Monitoring Net Zero Commitments',
    topics: ['Science-based target setting', 'Board KPIs for climate', 'Progress reporting'],
    duration: '2 hours'
  }],
  regulatoryAlignment: ['TCFD', 'ISSB', 'Companies Act', 'FRC Governance Code'],
  learningOutcomes: ['Establish board-level climate governance structures and committee mandates', 'Understand fiduciary duty obligations in relation to climate risk', 'Assess and mitigate director personal liability exposure from climate litigation', 'Fulfil TCFD and ISSB S1/S2 governance disclosure responsibilities', 'Engage effectively with institutional investors on climate strategy', 'Set and monitor science-based net zero commitments at board level'],
  certName: 'CarbonSafe Certified Climate Board Director (CCBD)',
  deliveryFormats: [{
    label: 'In-Person',
    desc: 'Executive venue, Kampala. 2 days. Small cohort format (max 12 participants). Includes private networking dinner.'
  }, {
    label: 'Online',
    desc: 'Live virtual sessions only. No self-paced option. 2 full-day sessions with the lead instructor.'
  }, {
    label: 'Private',
    desc: 'Delivered exclusively for your board. Minimum 5 directors. Custom scheduling.'
  }],
  caseStudies: [{
    title: 'Regional Bank Board',
    desc: 'Establishing a Board Climate Committee and TCFD governance framework for a listed East African bank.'
  }, {
    title: 'Energy Company Board',
    desc: 'Navigating director liability exposure following a climate-related shareholder resolution.'
  }]
}];